/**
 * Short-lived cache for booking guest document signed URLs.
 * Avoids repeat network calls when opening stay-guest drawers / switching guests.
 */

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();

function cacheKey(bookingId, guestId) {
  return `${bookingId}:${guestId}`;
}

export function getCachedBookingGuestDocuments(bookingId, guestId) {
  if (bookingId == null || guestId == null) return null;
  const entry = cache.get(cacheKey(bookingId, guestId));
  if (!entry) return null;
  if (Date.now() - entry.at > CACHE_TTL_MS) {
    cache.delete(cacheKey(bookingId, guestId));
    return null;
  }
  return entry.data;
}

export function setCachedBookingGuestDocuments(bookingId, guestId, data) {
  if (bookingId == null || guestId == null || data == null) return;
  cache.set(cacheKey(bookingId, guestId), { at: Date.now(), data });
}

export function clearCachedBookingGuestDocuments(bookingId, guestId) {
  if (bookingId == null) {
    cache.clear();
    return;
  }
  if (guestId == null) {
    const prefix = `${bookingId}:`;
    for (const key of cache.keys()) {
      if (key.startsWith(prefix)) cache.delete(key);
    }
    return;
  }
  cache.delete(cacheKey(bookingId, guestId));
}

/** In-flight promise dedupe so parallel callers share one request. */
const inflight = new Map();

export function getOrFetchBookingGuestDocuments(bookingId, guestId, fetcher) {
  const cached = getCachedBookingGuestDocuments(bookingId, guestId);
  if (cached) return Promise.resolve(cached);

  const key = cacheKey(bookingId, guestId);
  if (inflight.has(key)) return inflight.get(key);

  const promise = Promise.resolve()
    .then(() => fetcher(bookingId, guestId))
    .then((data) => {
      setCachedBookingGuestDocuments(bookingId, guestId, data);
      return data;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}
